/* GAH-PCT-01 - the discount-percentage WORDING test on the Golf At Home PDP,
   US only. Daniel: "Even though it is 60%, what if we just called it 63? Or
   61? Or 59?" Four arms, one label each: p60 (control, today's text), p63,
   p61, p59. PRICES NEVER CHANGE - the $119, the struck $299 and what checkout
   charges are identical in every arm; only the digits in front of "% OFF"
   move. Read the commit message before touching this: a displayed percentage
   that differs from the charged price is a discount claim that does not match
   the charge, and 59 is the only arm that understates it.

   This file owns applyPct() and nothing else. layout/theme.liquid's apply()
   decides the arm and writes it to <html data-gd-pct="63"> - the attribute is
   ABSENT for control, for the test being off, for every template but the GAH
   PDP and for every market but the US - then calls applyPct(). Nothing here
   reads a flag, so the arm can only ever come from one place, and a page with
   no attribute is restored to what Liquid rendered.

   Four facts about the nodes shape everything below:

   1. The buy-box badge and the desktop sticky-bar badge are DEAL-5 nodes:
      they carry data-gd-deal-a/b/c and assets/benchmark-deal-price.js:103
      rewrites their textContent to the arm-A string whenever the two differ -
      once at DOMContentLoaded and on bfcache pageshow today, every 60ms
      through a body childList observer the day dealTest flips true. A
      text-only rewrite would be undone the next time that follower looked.
      So: update data-gd-deal-a AND textContent to the same string in the same
      tick, and the follower finds nothing to correct. The same rule covers
      data-gd-a, which the GAH-DELTA-01 follower (benchmark-delta-price.js:52)
      owns on the bundle card's .lb-pill whenever the +$49 twin exists. Never
      add child nodes - both followers destroy child markup.
   2. The bundle card's .lb-pill is the BUNDLE's own derived save percentage
      (compare minus total over compare, benchmark-bundle-prices) - it reads
      "60% OFF" on live today by arithmetic, not by sharing the mat's literal.
      It is rewritten only while its rendered text is exactly the control
      label, so the day the bundle economics move it drops out of the test on
      its own instead of claiming the mat's figure.
   3. Only nodes whose ORIGINAL text is the control label are ever touched.
      The sale bar's text is an admin setting: if it is changed to "50% OFF"
      for some other sale, 63/61/59 are meaningless against it and this file
      must leave it alone rather than "correct" a different sale into this
      test's arm. The original is kept in data-gd-pct-orig on first sight so
      restore puts back exactly what Liquid rendered.
   4. Ownership is decided PER NODE, never per page. While gd-deal-b/c is on
      <html>, the DEAL-5 follower owns the text of every node carrying
      data-gd-deal-a (the badges, the pill) and prints that tier's own figure
      ("50% OFF" for the $149 mat); while gd-delta-b is up, the DELTA-01
      follower owns every node carrying data-gd-a (the bundle pill only - the
      mat's badges and the sale bar carry no data-gd-a, and the mat stays
      $119 / $299 under that test, so the label test is still valid on them).
      For an owned node the -a attribute is kept in step so a flip back to
      control lands right, but the text is left to the follower that owns it.
      An unowned node (the sale bars, which no follower touches) always takes
      this test's text. A page-wide "some price arm is live, skip every
      node" rule broke the attribute/text invariant of fact 1 on the badges
      (attribute rewritten, text not) and left the sale bar at 60 under
      badges the DEAL-5 follower had just flipped to 63. apply() disarms this
      test outright (no attribute) under a live DEAL-5 priced arm, so the
      unowned nodes restore to 60 there - a 63 against $149 is not a claim
      this test can make - and exposure says p60.
   5. The cart drawer's sale band is one of the nodes and it is re-rendered
      from SSR control HTML: cart-drawer.js swaps the whole .drawer__inner on
      the empty-state path, and renderContents / syncAdditionalProducts
      re-stamp cards on every cart mutation. A one-time rewrite would be
      undone by remove-then-re-add, so while an arm is on <html> a body
      childList observer (the benchmark-deal-price.js pattern, 60ms
      debounce) re-runs applyPct and the freshly SSR'd "60% OFF" is taken as
      that node's original and rewritten on first sight.

   Idempotent: runs from apply() (which fires in <head> before these nodes
   exist - a harmless no-op - and again whenever Intelligems' per-variation JS
   sets the flags, ~500ms in the live samples, inside the product-page shimmer
   window so the visitor normally never sees the swap), at script execution /
   DOMContentLoaded, on bfcache pageshow, and from the observer above. If the
   arm lands after the shimmer lifts, a visible 60 -> 63 swap is accepted by
   design; the badge is never hidden to cover it. */
(() => {
  'use strict';

  // benchmark-pdp-main is the only emitter today, but benchmark-pdp.js needed
  // this exact guard the day a second section started carrying its script tag
  if (window.__benchmarkPctLabelInit) return;
  window.__benchmarkPctLabelInit = true;

  // the label the page ships with today - the test is a wording test AROUND
  // this figure (fact 3), and it is also what apply() treats as "no arm"
  const CONTROL = '60';
  const LABEL = /^(\d{1,2})% OFF$/;

  // Every node on the GAH PDP that prints the mat's discount percentage - six
  // of them. .bb-price and .sb-price use the child combinator because the
  // desktop rail bundle card renders INSIDE .buybox (since bd1cf88) and its
  // pill is listed on its own line. .fm-modal .bm-badge matches nothing today
  // - the post-ATC modal prints "$119 · in your cart" and "+$39", no
  // percentage - it is here so a badge added to the modal later cannot say 60
  // under a buy box that just said 63. The cart drawer's sale band
  // (snippets/cart-drawer.liquid, a hard-coded "60% OFF" that renders on this
  // PDP because cart_type is drawer) is here for the same reason: it opens on
  // the click frame of Add to Cart, one tap after the badge, and it is the
  // figure the visitor carries to checkout. Not touched, by the brief: the
  // <s>$299, the $119, any "SAVE $" amount, meta/OG, JSON-LD, or anything
  // outside this PDP (the sale bars are sitewide markup, but apply() only
  // stamps the arm on this template).
  const SELECTORS = [
    '.bm-pdp .buybox .bb-price > .bm-badge',
    '.sticky-bar .sb-price > .bm-badge',
    '.fm-modal .bm-badge',
    '.header-benchmark__salebar-discount',
    '.cart-drawer__salebar-discount',
    '.lb-card .lb-pill',
  ].join(',');

  // fact 5 - the drawer re-renders its sale band from SSR HTML; re-run after
  // any DOM insertion while an arm is on <html>. Writing textContent is
  // itself a childList mutation, so the debounce plus "write only when it
  // differs" is what stops this from looping: the second pass changes nothing.
  let observerArmed = false;
  const armObserver = () => {
    if (observerArmed || !document.body) return;
    observerArmed = true;
    let queued = null;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = setTimeout(() => {
        queued = null;
        applyPct();
      }, 60);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const applyPct = () => {
    const arm = document.documentElement.dataset.gdPct;
    const nodes = document.querySelectorAll(SELECTORS);
    if (!nodes.length) return;
    // fact 4 - read at call time, never captured: the classes flip seconds
    // after load, and apply() writes them before it calls this
    const classes = document.documentElement.classList;
    const dealLive = classes.contains('gd-deal-b') || classes.contains('gd-deal-c');
    const deltaLive = classes.contains('gd-delta-b');
    nodes.forEach((node) => {
      // first sight: remember what Liquid rendered, refuse anything that is
      // not the control label (fact 3), and never look at that node again
      let orig = node.dataset.gdPctOrig;
      if (orig === undefined) {
        orig = node.textContent.trim();
        const match = LABEL.exec(orig);
        if (!match || match[1] !== CONTROL) return;
        node.dataset.gdPctOrig = orig;
      }
      const target = arm && arm !== CONTROL ? orig.replace(LABEL, `${arm}% OFF`) : orig;
      // attribute first, text second, same tick (fact 1). Only an attribute
      // that itself reads as a label is rewritten - a DEAL-5 "" (hide) or a
      // "$119" would never be on one of these nodes, but the guard costs
      // nothing and keeps this file from ever inventing a price string.
      ['gdDealA', 'gdA'].forEach((key) => {
        const current = node.dataset[key];
        if (current !== undefined && LABEL.test(current) && current !== target) node.dataset[key] = target;
      });
      // fact 4 - THIS node's text belongs to a live price follower: its -a
      // attribute is in step now, and writing text here would only be
      // flipped back 60ms later by that follower's observer
      if (dealLive && node.dataset.gdDealA !== undefined) return;
      if (deltaLive && node.dataset.gdA !== undefined) return;
      if (node.textContent !== target) node.textContent = target;
    });
    // fact 5 - only once this page has carried a non-control arm: a freshly
    // SSR'd node already reads 60, so p60 and the test being off cost live
    // traffic no observer. Once armed it stays armed - a later flip back to
    // 60 is applied by apply() directly and the observer then finds nothing
    // to change.
    if (arm !== undefined && arm !== CONTROL) armObserver();
  };

  window.GD_FLAGS = window.GD_FLAGS || {};
  window.GD_FLAGS.applyPct = applyPct;

  // bfcache: mobile Safari restores the frozen DOM - stale text, live attribute
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) applyPct();
  });

  // defer scripts execute with readyState 'interactive', so this normally runs
  // right here - before benchmark-deal-price.js's DOMContentLoaded pass, which
  // then finds attribute and text already agreeing (fact 1)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyPct());
  } else {
    applyPct();
  }
})();
